<template>
  <article
   class="h-full flex flex-col bg-white overflow-hidden rounded-lg shadow-card"
  >
    <div class="w-full h-12.5 flex items-center pl-5" :class="status.color">
      <p class="font-primary font-extrabold text-20 leading-1">Status: {{ status.description }}</p>
    </div>
    <div class="is-fixed-ar is-1x1" >
      <a :href="adventureUrl" class="ar-inside">
        <picture v-if="booking.image">
          <source :srcset="`${booking.image.urls.square_403} 403w`" media="(min-width: 1280px)">
          <source :srcset="`${booking.image.urls.square_403} 403w`" media="(max-width: 639px)">
          <source :srcset="`${booking.image.urls.square_384} 384w`" media="(max-width: 404px)">
          <img :src="booking.image.urls.square_384" :alt="altText" class="full-bg-cover bg-grey-mid" />
        </picture>
        <p v-if="booking.duration" class="absolute bottom-5 left-5 font-primary font-extrabold text-white-off text-20">{{ duration }}</p>
        <div class="absolute bottom-2.5 right-2.5">
          <grading-badge :grade="grade"></grading-badge>
        </div>
      </a>
    </div>
    <div class="flex flex-col flex-grow">
      <div class="min-h-130 bg-blue-brand px-5 py-6">
        <p class="font-primary font-extrabold text-20 text-white mb-2.5">{{ name }}</p>
        <p class="h-3 font-primary font-extrabold text-12 text-white uppercase leading-1">{{ country }}<span v-if="country && type"> - </span>{{ type }}</p>
      </div>
      <div class="p-5 flex flex-col flex-grow">
        <p class="flex-grow font-sans text-16 leading-1.63 pb-5 mb-5 border-b border-white-pale">{{ description }}</p>
        <div class="flex">
          <div class="w-1/2 text-center">
            <p class="font-primary font-extrabold text-center text-14">Paid</p>
            <p class="font-primary font-extrabold text-center text-28">
              <display-price :price-gbp="paidAmountGbp" :price-usd="paidAmountUsd" :force-currency="booking.currency"></display-price>
            </p>
          </div>
          <div class="w-1/2 text-center">
            <p class="font-primary font-extrabold text-center text-14" :class="status.outstanding_label">Outstanding</p>
            <p class="font-primary font-extrabold text-center text-28" :class="status.outstanding_amount">
              <display-price :price-gbp="outstandingAmountGbp" :price-usd="outstandingAmountUsd" :force-currency="booking.currency"></display-price>
            </p>
          </div>
        </div>
      </div>
      <div class="flex flex-grow-0 w-full px-2.5">
        <a :href="adventureUrl" class="button w-full">Details</a>
      </div>
      <div v-if="months" class="flex items-center justify-evenly w-full px-0.5 my-2.5">
        <template v-for="index in 12">
          <svg-sprite :key="index" :href="monthIcon(index)" class="h-6 w-6"></svg-sprite>
        </template>
      </div>
    </div>
  </article>
</template>

<script>
import Route from '../src/Route';
import get from 'lodash/get';
import GradingBadge from './GradingBadge.vue';
import SvgSprite from '../vendor/enso/components/SvgSprite.vue';
import DisplayPrice from './DisplayPrice.vue';
import { parseISO } from 'date-fns';

export default {
  components: { GradingBadge, SvgSprite, DisplayPrice },

  props: {
    booking: {
      type: Object,
      required: true,
    },
  },

  computed: {
    adventureUrl() {
      return Route('account.bookings.show', { 'booking': this.booking.uuid });
    },

    imageUrl() {
      return get(this.booking, 'image.url');
    },

    altText() {
      return get(this.booking, 'image.alt_text');
    },

    name() {
      return get(this.booking, 'adventure_name');
    },

    country() {
      return get(this.booking, 'country');
    },

    type() {
      return get(this.booking, 'adventure_type');
    },

    description() {
      return get(this.booking, 'description');
    },

    duration() {
      if (this.booking.duration === 1) {
        return `${this.booking.duration} Day`
      }
      else {
        return `${this.booking.duration} Days`
      }
    },

    grade() {
      return this.booking.grade;
    },

    months() {
      return this.booking.months;
    },

    paidAmountGbp() {
      return this.booking.amount_paid_gbp;
    },

    outstandingAmountGbp() {
      return this.booking.amount_outstanding_gbp;
    },

    paidAmountUsd() {
      return this.booking.amount_paid_usd;
    },

    outstandingAmountUsd() {
      return this.booking.amount_outstanding_usd;
    },

    status() {
      const today = new Date;
      const adventureStart = parseISO(this.booking.start_date);
      const adventureEnd = parseISO(this.booking.end_date);

      if (this.booking.needs_approval) {
        return {
          color: 'bg-yellow-brand text-blue-dark',
          description: 'Pending approval',
          outstanding_label: 'opacity-50',
          outstanding_amount: 'opacity-50',
        };
      }
      else if (this.outstandingAmountGbp > 0 || this.outstandingAmountUsd > 0) {
        return {
          color: 'bg-yellow-brand text-blue-dark',
          description: 'Outstanding balance',
          outstanding_label: '',
          outstanding_amount: 'text-red-brand',
        };
      }
      else if (today < adventureStart) {
        return {
          color: 'bg-blue-brand text-white',
          description: 'Paid',
          outstanding_label: '',
          outstanding_amount: '',
        };
      }
      else if (today >= adventureStart && today <= adventureEnd) {
        return {
          color: 'bg-green-brand text-white',
          description: 'On holiday',
          outstanding_label: '',
          outstanding_amount: '',
          };
      }
      else {
        return {
          color: 'bg-blue-brand text-white',
          description: 'Completed',
          outstanding_label: '',
          outstanding_amount: '',
        };
      }
    },
  },

  methods: {
    monthIcon(index) {
      if (this.months.find( el => el === index)) {
        return `icon-month-${index}-selected`;
      } else {
        return `icon-month-${index}`;
      }
    },
  },
}
</script>
